import PropTypes from "prop-types";
import React, { useEffect, useState, useMemo } from "react";
import { Box, Collapse, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import { menuItems } from "../menu";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo.svg";

const DrawerList = ({ menus, sidebarCollapse }) => {
  return (
    <Box sx={{ borderTop: "1px solid #D1D5DB80" }} role="presentation">
      <List sx={{ p: 1 }}>
        {menus.map((menu) => (
          <MenuItem key={menu.id} menu={menu} sidebarCollapse={sidebarCollapse} />
        ))}
      </List>
    </Box>
  );
};

const MenuItem = ({ menu, sidebarCollapse }) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  // Memoized active states to prevent unnecessary re-renders
  const isActive = useMemo(
    () => menu.link && location.pathname.startsWith(menu.link),
    [menu.link, location.pathname]
  );

  const isParentActive = useMemo(
    () =>
      menu.type === "collapse" &&
      menu.items.some((subMenu) => location.pathname.startsWith(subMenu.link)),
    [menu.type, menu.items, location.pathname]
  );

  // Open parent menu if it's active
  useEffect(() => {
    setOpen(isParentActive);
  }, [isParentActive]);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setHover(true)
      const drawer = document.querySelector(".MuiDrawer-paper");
      if (drawer) {
        drawer.style.overflowY = "unset";
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setHover(false)
      const drawer = document.querySelector(".MuiDrawer-paper");
      if (drawer) {
        drawer.style.overflowY = "auto";
      }
    }
  };

const handleToggle = () => {
  if (!sidebarCollapse || isMobile) setOpen((prev) => !prev);
};

const handleClick = () => {
  if (menu.link) navigate(menu.link); // ✅ now both "item" and "collapse" with link will navigate
  if (menu.type === "collapse" && (!sidebarCollapse || isMobile)) {
    setOpen((prev) => !prev); // toggle submenu only when expanded
  }
};


  const styles = {
    button: {
      borderRadius: "6px",
      padding: "12px",
      justifyContent: "center",
      backgroundColor: isActive || isParentActive ? "#487FFF" : "transparent",
      color: isActive || isParentActive ? "#FFFFFF" : "#273142",
      "& .MuiListItemIcon-root": {
        color: isActive || isParentActive ? "#FFFFFF" : "#273142",
      },
      "&:hover": {
        backgroundColor: "#487FFF",
        color: "#FFFFFF",
        "& .MuiListItemIcon-root": {
          color: "#FFFFFF",
        },
      },
    },
    collapseMenu: {
      position: "absolute",
      left: "100%",
      top: 0,
      backgroundColor: "#FFF",
      boxShadow: "2px 2px 10px rgba(0,0,0,0.1)",
      borderRadius: "6px",
      zIndex: 1000,
      minWidth: "180px",
      padding: "8px",
    },
  };

  if (menu.type === "item") {
    return (
      <ListItem disablePadding sx={{ my: 1 }}>
        <ListItemButton onClick={handleClick} sx={styles.button}>
          <ListItemIcon sx={{ minWidth: "auto" }}>{menu.icon}</ListItemIcon>
          {(!sidebarCollapse || isMobile) && (
            <ListItemText
              primary={<Typography variant="h6" sx={{ pl: 1.5 }}>{menu.title}</Typography>}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  }

  if (menu.type === "collapse") {
    return (
      <Box sx={{ position: "relative", my: 1 }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleClick} sx={styles.button}>
            <ListItemIcon sx={{ minWidth: "auto" }}>{menu.icon}</ListItemIcon>
            {(!sidebarCollapse || isMobile) && (
              <ListItemText
                primary={<Typography variant="h6" sx={{ pl: 1.5 }}>{menu.title}</Typography>}
              />
            )}
            {(!sidebarCollapse || isMobile) ? (open ? <ExpandLess /> : <NavigateNextOutlinedIcon />) : null}
          </ListItemButton>
        </ListItem>

        {/* Collapsed Menu */}
        {(!sidebarCollapse || isMobile) && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List sx={{ pl: "20px" }}>
              {menu.items.map((subMenu) => (
                <MenuItem key={subMenu.id} menu={subMenu} sidebarCollapse={sidebarCollapse} />
              ))}
            </List>
          </Collapse>
        )}

        {/* Hover-Triggered Submenu */}
        {sidebarCollapse && hover && (
          <Box sx={styles.collapseMenu}>
            <List>
              {menu.items.map((subMenu) => (
                <MenuItem key={subMenu.id} menu={subMenu} sidebarCollapse={false} />
              ))}
            </List>
          </Box>
        )}
      </Box>
    );
  }

  return null;
};

const SideBar = ({ sidebarToggleCollapse, sidebarCollapse }) => {
  const isMobile = useMediaQuery("(max-width: 767px)");

  return (
    <Drawer
      sx={{
        ".MuiDrawer-paper": {
          width: isMobile ? 250 : sidebarCollapse ? 60 : 276,
          transition: "width 0.3s ease",
          top: isMobile ? 72 : 0,
        },
      }}
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? sidebarCollapse : true}
      onClose={sidebarToggleCollapse}
      anchor="left"
    >
      {!isMobile && (
        <Box sx={{ p: 2, height: 72 }}>
          <img
            src={sidebarCollapse ? logo : logo}
            alt="Logo"
            style={{ height: "100%", width: "auto", maxWidth: "140px" }}
          />
        </Box>
      )}
      <DrawerList menus={menuItems} sidebarCollapse={sidebarCollapse} />
    </Drawer>
  );
};

SideBar.propTypes = {
  sidebarCollapse: PropTypes.bool,
  sidebarToggleCollapse: PropTypes.func,
};

export default SideBar;
